import { HttpClient, HttpErrorResponse, HttpEventType, HttpHeaders, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { filter, switchMap, take, catchError, tap } from 'rxjs/operators';
import { UserService } from 'src/app/core/common-services/user.services';
import { ERiskLevel, RefreshTokenInputDto } from 'src/app/core/data-models/identity.data.models';
import { environment } from 'src/environments/environment';
import { LanguageService } from './language.service';
import { NotificationService } from 'src/app/core/common-services/notification.service';
import { KycStatusService } from './kyc-status.service';
import { TenantConfigService } from './tenant-config.service';

@Injectable({
  providedIn: 'root'
})
export class BaseApiService<T> {

  private isRefreshTokenInProgress: boolean = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(
    private httpClient: HttpClient,
    private router: Router,
    private userService: UserService,
    private languageService: LanguageService,
    private notificationService: NotificationService,
    private kycStatusService: KycStatusService,
    private tenantConfigService: TenantConfigService
  ) { }

  public Post(destinationUrl: string, body: any, options?: {}): Observable<T> {
    return this.request<T>('post', destinationUrl, body, options);
  }

  public Put(destinationUrl: string, body: any, options?: {}): Observable<T> {
    return this.request<T>('put', destinationUrl, body, options);
  }

  public Get(destinationUrl: string, options?: {}): Observable<T> {
    return this.request<T>('get', destinationUrl, undefined, options);
  }

  public Delete(destinationUrl: string, options?: {}): Observable<T> {
    return this.request<T>('delete', destinationUrl, undefined, options);
  }

  private request<T>(method: string, url: string, body: any, options?: any): Observable<T> {
    url = `${this.tenantConfigService.tenant.apisBaseUrl}/api/1/${url}`;
    if (this.shouldRefreshToken(url)) {
      return this.refreshToken().pipe(

        switchMap(() => this.performRequest<T>(method, url, body, options)),
        catchError(error => {


          if (error.status === 401) {
            this.refreshToken()
          }
          return throwError(error);
        })
      );
    } else {
      return this.performRequest<T>(method, url, body, options);
    }
  }

  private shouldRefreshToken(url: string): boolean {
    return this.isRefreshTokenInProgress === false &&
      this.userService.isLoggedIn() &&
      this.userService.currentUserCredantials.expiryTokenDate &&
      new Date(this.userService.currentUserCredantials.expiryTokenDate).getTime() <= new Date().getTime() &&
      !url.includes('RefreshToken') &&
      !url.includes('Login') &&
      !url.includes('Logout');
  }

  private performRequest<T>(method: string, url: string, body: any, options?: any): Observable<T> {
    if (options == undefined) options = {};

    let headers = options.headers || new HttpHeaders();
    const acceptLanguage = this.languageService.currentLanguage == 'ar' ? 'ar-SA' : 'en-US';
    headers = headers.set('Accept-Language', acceptLanguage);

    const token = this.userService.currentUserCredantials?.token;
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    options.headers = headers;

    return new Observable((subscriber) => {
      this.httpClient.request<T>(new HttpRequest(method, url, body, options)).subscribe(
        (response: any) => {
          if (response.type == HttpEventType.Response) {
            subscriber.next(response.body);
            // complete like HttpClient does, otherwise forkJoin/lastValueFrom never emit
            subscriber.complete();
          }
        },
        (error: any) => {
          try {
            if (error.status == 403) {
              if (error.error.errorCode === 'ExpirationDate') {
                this.notificationService.error(error.error.message);
                this.userService.logout();
              }
              else if (error?.error?.statusCode == 401 && (error?.error?.message == `You Don't Have Privilege For This Action` || error?.error?.message == `ليس لديك صلاحية لهذا الأجراء`)) {
                this.notificationService.error(error.error.message);
                setTimeout(() => {
                  this.router.navigate(['/kyc/kyc-status']);
                }, 500);
              }
              else if (error?.error?.statusCode == 9001) {
                this.userService.currentUserCredantials = { ...this.userService.currentUserCredantials, userKYCStatus: 'Pending' };
                this.router.navigate(['/kyc/kyc-status']);
              }
              else if (error?.error?.statusCode == 9002) {
                this.userService.currentUserCredantials = { ...this.userService.currentUserCredantials, userKYCStatus: 'ReSubmit' };
                this.router.navigate(['/kyc/kyc-status']);
              }
              else if (error?.error?.statusCode == 9003) {
                this.userService.currentUserCredantials = { ...this.userService.currentUserCredantials, userKYCStatus: 'Rejected' };
                this.router.navigate(['/kyc/kyc-status']);
              }
              else if (error?.error?.statusCode == 9004) {
                this.userService.currentUserCredantials = { ...this.userService.currentUserCredantials, userKYCStatus: 'Expired' };
                this.kycStatusService.openKycExpiredDialog();
              }
                else if (error?.error?.statusCode == 9005) {
                this.userService.currentUserCredantials = { ...this.userService.currentUserCredantials,kycRiskLevel: ERiskLevel.High };
                this.kycStatusService.openKycHighRiskDialog();
              }
              else
                subscriber.error(error);

            }

            else if (!(error.error instanceof Blob)) {

              if (error.status === 0 || error.status === 401) {
                if (!this.isRefreshTokenInProgress)
                  this.refreshToken();
                else
                  this.userService.logout();

                // Network failure (offline, DNS, CORS): surface it so callers can show an error
                if (error.status === 0)
                  subscriber.error(error);
              }
              else
                subscriber.error(error);

            }
            else if (error instanceof HttpErrorResponse && error.status === 401) {
              this.refreshToken();


            }
          }
          catch (ex) {
            throw ex;
          }
        }
      );
    });
  }

  private refreshToken(): Observable<any> {
    if (this.isRefreshTokenInProgress) {
      return this.refreshTokenSubject.pipe(
        filter(result => result !== null),
        take(1)
      );
    } else {
      this.isRefreshTokenInProgress = true;
      this.refreshTokenSubject.next(null);

      const refreshTokenInput: RefreshTokenInputDto = {
        phoneNumber: this.userService.currentUserCredantials.phoneNumber!,
        refreshToken: this.userService.currentUserCredantials.refreshToken!
      };

      const refreshTokenApiUrl = `${this.tenantConfigService.tenant.apisBaseUrl}/api/1/User/RefreshToken`;
      return new Observable((subscriber) => {
        let headers = new HttpHeaders();
        const token = this.userService.currentUserCredantials?.token;
        headers = headers.set('Authorization', `Bearer ${token}`);
        this.httpClient.post(refreshTokenApiUrl, refreshTokenInput,{headers:headers}).subscribe(
          (res: any) => {

            this.isRefreshTokenInProgress = false;
            if (res.data) {
              this.userService.currentUserCredantials = res.data;
              this.refreshTokenSubject.next(res.data);
              subscriber.next(res.data);
              subscriber.complete();
            } else {
              this.userService.logout();
              subscriber.complete();
            }
          },
          (error) => {
            this.isRefreshTokenInProgress = false;
            this.userService.logout();
            this.refreshTokenSubject.next(null);
            subscriber.error(error);
          }
        );
      });
    }
  }




}
